<template>
  <div id="app" class="text-white">
    <div v-if="loading">
      <div class="w-64 mx-auto mt-12" role="status">
        <div>
          <img
            src="/reeltrack-orange.svg"
            alt="ReelTrack"
            class="h-24"
            width="256"
            height="96"
          />
        </div>
        <Loading class="mt-6 m-auto w-12" size="2xl" />
        <span class="sr-only">{{ $t("loading") }}</span>
      </div>
    </div>
    <div v-else>
      <Authenticated v-if="isAuthenticated" />
      <Unauthenticated v-else />
    </div>
  </div>
</template>

<script>
import { Locale } from "./i18n";
import { ViewName } from "@/router";
import { localeOf } from "@/utils/user";
import Authenticated from "@/views/Authenticated.vue";
import Unauthenticated from "@/views/Unauthenticated.vue";

export default {
  name: "App",
  components: { Authenticated, Unauthenticated },

  data() {
    return {
      loading: true,
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    isAuthenticated() {
      return !!this.$store.getters.authToken;
    },
    isOnLoginPage() {
      return (
        this.$route.name === ViewName.Start ||
        this.$route.name === ViewName.UserLogin
      );
    },
  },

  mounted() {
    // Locale from the last local choice; replaced by the user's saved
    // language once the account loads below.
    this.applyLocale(localStorage.getItem("rt-locale") ?? Locale.ENGLISH);

    // Refresh user if we have a token but state is cleared.
    // (Example: happens when closing the website and opening it again.)
    if (this.isAuthenticated && this.$store.getters.authUser === null) {
      this.$store
        .dispatch("getAuthenticatedUser")
        .then((user) => {
          this.$store.dispatch("fetchAll");
          this.applyLocale(localeOf(user));
          this.loading = false;
          // Only bounce to the dashboard when we came in on a login page.
          // Anything else is a refresh or a deep link and must stay put.
          if (this.isOnLoginPage) {
            this.$router
              .push({ name: ViewName.Dashboard })
              .catch(() => undefined);
          }
        })
        .catch(() => {
          // Token was rejected (interceptor logs us out); show the login page.
          this.loading = false;
        });
    } else {
      this.loading = false;
      if (!this.isAuthenticated && !this.$route.meta.public) {
        // The router guard normally handles this and preserves ?redirect=;
        // this covers a token cleared while the app was already open.
        this.$router.push({ name: ViewName.Start }).catch(() => undefined);
      }
    }
  },

  methods: {
    applyLocale(locale) {
      if (!locale) return;
      this.$root.$i18n.locale = locale;
      // Keep <html lang> in sync so screen readers use the right voice.
      document.documentElement.setAttribute("lang", locale);
    },
  },
};
</script>

<!--
  Global transitions and focus styles live in assets/styles/index.css.
  A blanket `* { transition: all }` used to live here; it animated layout
  properties on every node and made state changes feel laggy.
-->
